import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { ModalController, Platform } from '@ionic/angular';
import { MobilePlatformVersions } from '@wilson/api';
import { VersioningService } from '@wilson/api/gateway';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, filter, first, map } from 'rxjs/operators';
import {
  DeviceUpdateComponent,
  OutdatedDeviceComponent,
} from './components/device-update/device-update.component';
import { VersionUpdateComponent } from './components/version-update/version-update.component';
import { isBelowVersion } from './is-below-version';
import { lt, valid } from 'semver';

const DEVICE_WARNING_STORAGE_KEY = 'deviceWarningThresholds';

@Injectable({
  providedIn: 'root',
})
export class VersionUpdateService {
  private readonly versioningService = inject(VersioningService);
  private readonly platform = inject(Platform);
  private readonly router = inject(Router);
  private readonly modalCtrl = inject(ModalController);

  validatedCurrentVersion!: boolean;

  initAppVersionCheck() {
    if (Capacitor.isNativePlatform()) {
      combineLatest([this.getLocalVersion(), this.getRemoteVersionConfig()])
        .pipe(
          first(),
          filter(
            ([localVersion, remoteVersionConfig]: [
              string,
              MobilePlatformVersions,
            ]) =>
              !!localVersion &&
              !!valid(localVersion) &&
              !!remoteVersionConfig &&
              !!valid(remoteVersionConfig?.maxVersion) &&
              !!valid(remoteVersionConfig?.minVersion) &&
              !!valid(remoteVersionConfig?.minAPIVersion),
          ),
        )
        .subscribe(([localVersion, remoteVersionConfig]) =>
          this.onDidGetVersionData(localVersion, remoteVersionConfig),
        );
    }
    this.router.navigate(['/tabs/home']);
  }

  onDidGetVersionData(
    localVersion: string,
    remoteVersionConfig: MobilePlatformVersions,
  ) {
    // TODO: replace local storage with storage service
    this.validatedCurrentVersion =
      JSON.parse(localStorage.getItem('versionConfig'))?.maxVersion ===
      remoteVersionConfig?.maxVersion;

    if (remoteVersionConfig) {
      localStorage.setItem(
        'versionConfig',
        JSON.stringify(remoteVersionConfig),
      );
    }

    this.validateVersion(localVersion, remoteVersionConfig);
  }

  getLocalVersion() {
    return Capacitor.isNativePlatform()
      ? from(App.getInfo()).pipe(map(({ version }) => version))
      : of(null);
  }

  getRemoteVersionConfig() {
    const remoteVersionConfig$: Observable<MobilePlatformVersions> =
      this.versioningService.getMobileVersions().pipe(
        map((remoteVersionConfig) => {
          let versionConfig: MobilePlatformVersions;

          if (this.platform.is('ios')) {
            versionConfig = remoteVersionConfig.ios;
          } else if (this.platform.is('android')) {
            versionConfig = remoteVersionConfig.android;
          }
          return versionConfig;
        }),
        catchError(() => {
          return of(JSON.parse(localStorage.getItem('versionConfig')));
        }),
      );

    return remoteVersionConfig$;
  }

  validateVersion(
    localVersion: string,
    remoteVersionConfig: MobilePlatformVersions,
  ) {
    const { maxVersion, minVersion, storeUrl } = remoteVersionConfig;

    if (lt(localVersion, minVersion)) {
      this.presentRequiredUpdateModal(storeUrl);
    } else if (lt(localVersion, maxVersion) && !this.validatedCurrentVersion) {
      this.presentUpdateModal(storeUrl);
    } else {
      this.validateDeviceVersion(remoteVersionConfig);
    }
  }

  /**
   * Warns users whose OS or Android System WebView is below the configured
   * warnBelow* version, e.g. ahead of raising the native minimum versions.
   * This runs in the WebView, so it can only warn; blocking happens natively
   * (iOS deployment target, Capacitor's android.minWebViewVersion).
   * Like maxVersion, it is shown once per configured threshold.
   */
  async validateDeviceVersion({
    warnBelowOsVersion,
    warnBelowWebViewVersion,
  }: MobilePlatformVersions) {
    try {
      // TODO: replace local storage with storage service
      const thresholds = JSON.stringify({
        warnBelowOsVersion,
        warnBelowWebViewVersion,
      });
      if (localStorage.getItem(DEVICE_WARNING_STORAGE_KEY) === thresholds) {
        return;
      }

      const { osVersion, webViewVersion } = await Device.getInfo();
      let outdatedComponent: OutdatedDeviceComponent | null = null;

      if (isBelowVersion(osVersion, warnBelowOsVersion)) {
        outdatedComponent = 'os';
      } else if (
        this.platform.is('android') &&
        isBelowVersion(webViewVersion, warnBelowWebViewVersion)
      ) {
        outdatedComponent = 'webView';
      }

      if (outdatedComponent) {
        localStorage.setItem(DEVICE_WARNING_STORAGE_KEY, thresholds);
        await this.presentDeviceUpdateModal(outdatedComponent);
      }
    } catch (error) {
      console.warn('Failed to validate device version', error);
    }
  }

  async presentUpdateModal(storeUrl: string) {
    const modal = await this.modalCtrl.create({
      component: VersionUpdateComponent,
      componentProps: { storeUrl },
    });

    modal?.present();
  }

  async presentRequiredUpdateModal(storeUrl: string) {
    const modal = await this.modalCtrl.create({
      component: VersionUpdateComponent,
      backdropDismiss: false,
      componentProps: { updateRequired: true, storeUrl },
    });

    modal?.present();
  }

  async presentDeviceUpdateModal(outdatedComponent: OutdatedDeviceComponent) {
    const modal = await this.modalCtrl.create({
      component: DeviceUpdateComponent,
      componentProps: { outdatedComponent },
    });

    modal?.present();
  }
}
